import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useToast } from '@chakra-ui/react';
import ModalComponent from '../Modal';
import ServerSideDatatable from '../ServerSideDatatable';
import { currancyFormatter } from '../../utils/helper';
import { getSiteAssociationMargins } from '../../redux/action/association.action';
import { TOAST_SETTINGS } from '../../constants';

const AssociationLosModal = ({ isOpen, onClose, associationId, selectedSite }) => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTable] = useState(false);
  const { user_details: userDetails } = useSelector((state) => state.authentication);
  const [columns] = useState([
    {
      label: 'LOS',
      field: 'los',
      sort: false,
      customRow: (cell) => {
        const los = parseFloat(cell, 10);
        // if cell is lees than 1 then months else years
        if (los < 1) {
          const months = parseInt(cell.split('.')[1], 10);
          return `${months} ${months > 1 ? 'Months' : 'Month'}`;
        }

        if (los >= 1 && los <= 20) {
          return `${los} ${los > 1 ? 'Years' : 'Year'}`;
        }

        return '-';
      },
    },
    {
      label: 'Margin',
      field: 'margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Overtime Margin',
      field: 'overtime_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Transport Fee',
      field: 'transport_fee',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Miscellaneous',
      field: 'ssp',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Training Margin',
      field: 'training_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Bh Margin',
      field: 'bh_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'NSP Margin',
      field: 'nsp_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Supervisor Standard Margin',
      field: 'supervisor_standard_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Supervisor Overtime Margin',
      field: 'supervisor_overtime_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Supervisor Permanent Margin',
      field: 'supervisor_permanent_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Suspension Margin',
      field: 'suspension_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Induction Training Margin',
      field: 'induction_training_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
  ]);

  const handlePaginate = useCallback(
    async (size, pageNumber, sortKey, sorting) => {
      setIsLoading(true);
      try {
        const result = await getSiteAssociationMargins(
          associationId,
          selectedSite.siteId,
          size,
          pageNumber,
          sortKey,
          sorting
        );
        if (result.ok) {
          return {
            data: {
              losMargins: result.data.margins_list,
              count: result.data.total,
            },
          };
        }
        toast({
          ...TOAST_SETTINGS.SETTINGS,
          status: TOAST_SETTINGS.ERROR,
          title: result.error,
        });
        return {
          data: {
            losMargins: [],
            count: 0,
          },
        };
      } catch (error) {
        return {
          data: {
            losMargins: [],
            count: 0,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [selectedSite]
  );

  if (!selectedSite) {
    return null;
  }

  return (
    <ModalComponent
      specifySize="6xl"
      isOpen={isOpen}
      onClose={onClose}
      modalTitle={`LOS Margins - ${selectedSite.siteName}`}
      modalContent={
        <ServerSideDatatable
          pageSize={10}
          initialSortKey="site_name"
          column={columns}
          onPaginate={handlePaginate}
          afterPaginateData={() => setIsLoading(false)}
          pagination
          pages={[5, 10, 15]}
          dataKey="losMargins"
          refresh={refreshTable}
          isLoading={isLoading}
        />
      }
      negative={null}
      positive={null}
    />
  );
};

export default AssociationLosModal;
